import { NuxtHTTPInstance } from '@nuxt/http';
import { computed, ref, useAsync } from '@nuxtjs/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import apiUrls from '@/apiUrls.config.js';
import getSlides from '~/components/biura/functions/swiper-functions/getSlides';
import GoogleMapTypeEnum from '~/components/map/types/googleMapTypes';
import useMetaTagsService from '~/composables/useMetaTagsService';
import { UseRainbowContext } from '~/context/RainbowContext';
import buildJsonLdForBiuraSzczegoly from '~/jsonLd/functions/buildJsonLdForBiuraSzczegoly';

import fillMapIcons from '../functions/fillMapIcons';
import { useRoutePathChange } from '../functions/shared';
import IBiuroSzczegoly from '../types/IBiuroSzczegoly';

const { useState } = createNamespacedHelpers('biura');

const getBiuro = ($http: NuxtHTTPInstance, biuroId: number) => {
  const url = `${apiUrls.biuraPobierzSzczegolyBiura}?biuro=${biuroId}`;

  return $http.$get<IBiuroSzczegoly>(url);
};

const useBiuraSzczegolyService = () => {
  const { routeInfo } = useState(['routeInfo']);
  const { route, $http } = UseRainbowContext();

  const showMapModal = ref<boolean>(false);
  const biuro = useAsync<IBiuroSzczegoly>(() => getBiuro($http, routeInfo.value.DodatkowePola.biuroId));

  const updateBiuro = async () => {
    biuro.value = null;
    biuro.value = await getBiuro($http, routeInfo.value.DodatkowePola.biuroId);
  };

  fillMapIcons();
  useRoutePathChange(updateBiuro);

  const slides = computed(() => (biuro.value ? getSlides(ref(biuro.value.Zdjecia)) : []));
  const shouldRenderLokalizacjaOpis = computed(() => {
    if (biuro.value?.LokalizacjaOpis && biuro.value?.LokalizacjaOpis.length > 8) return biuro.value?.LokalizacjaOpis && biuro.value?.LokalizacjaOpis.trim() !== '';

    return undefined;
  });
  const shouldRenderPracownicyBiuraPolecajaOpis = computed(
    () => biuro.value?.PracownicyBiuraPolecajaOpis && biuro.value?.PracownicyBiuraPolecajaOpis.trim() !== '',
  );
  const shouldRenderOpisSection = computed(() => shouldRenderLokalizacjaOpis.value || shouldRenderPracownicyBiuraPolecajaOpis.value);

  return {
    biuro,
    slides,
    showAllLink: computed((): string => `/biura/${route.value.params.miasto}`),
    showMapModal,
    toggleMapModal: (): void => {
      showMapModal.value = !showMapModal.value;
    },
    mapType: computed(() => GoogleMapTypeEnum['BIURO-SZCZEGOLY']),
    shouldRenderImageSwiper: computed(() => slides.value && slides.value.length),
    shouldRenderLokalizacjaOpis,
    shouldRenderPracownicyBiuraPolecajaOpis,
    shouldRenderOpisSection,
    markers: computed(() => (biuro.value ? [biuro.value] : [])),
    route,
    metaTags: computed(() => useMetaTagsService({ route, routeInfo })?.metaTags),
    jsonLd: computed(() => buildJsonLdForBiuraSzczegoly({ biuroRef: biuro, routeInfoRef: routeInfo, host: 'https://r.pl' })),
  };
};
export default useBiuraSzczegolyService;
